import { Component, OnInit, OnDestroy } from '@angular/core'
import { EnterpriseApiService } from 'src/app/services/enterprise-api.service'
import { EnterpriseContextService } from 'src/app/services/enterprise-context.service'
import { ApiTokenListItem } from '@parkupp/core'
import { Subscription } from 'rxjs'
import Swal from 'sweetalert2'

@Component({
    selector: 'app-admin-api-management',
    templateUrl: './admin-api-management.component.html',
    styleUrls: ['./admin-api-management.component.scss'],
})
export class AdminApiManagementComponent implements OnInit, OnDestroy {
    // State
    isLoading = true
    isCreating = false
    tokens: ApiTokenListItem[] = []
    
    // For creating new token
    newTokenName = ''
    newTokenDescription = ''
    
    // After token creation, store the plain token to display once
    createdPlainToken: string | null = null

    // Subscription management
    private enterpriseSubscription?: Subscription

    constructor(
        private apiService: EnterpriseApiService,
        public enterpriseContext: EnterpriseContextService
    ) {}

    ngOnInit(): void {
        // Subscribe to enterprise changes to refresh data
        this.enterpriseSubscription = this.enterpriseContext.currentEnterpriseKey$.subscribe(() => {
            this.createdPlainToken = null // Clear any displayed token
            this.tokens = []
            this.loadTokens()
        })
    }

    ngOnDestroy(): void {
        this.enterpriseSubscription?.unsubscribe()
    }

    get enterpriseKey(): string {
        return this.enterpriseContext.currentEnterpriseKey || ''
    }

    get apiDocsUrl(): string {
        return this.apiService.getApiDocsUrl(this.enterpriseKey)
    }

    /**
     * Load all tokens for the current enterprise
     */
    async loadTokens(): Promise<void> {
        if (!this.enterpriseKey) {
            this.isLoading = false
            return
        }

        this.isLoading = true
        try {
            this.tokens = await this.apiService.listTokens(this.enterpriseKey)
        } catch (error: any) {
            console.error('Error loading tokens:', error)
            Swal.fire({
                title: 'Error',
                text: 'Failed to load API tokens',
                icon: 'error',
            })
        } finally {
            this.isLoading = false
        }
    }

    /**
     * Create a new API token
     */
    async createToken(): Promise<void> {
        if (!this.enterpriseKey) {
            Swal.fire({
                title: 'No Enterprise Selected',
                text: 'Please select an enterprise from the dropdown above',
                icon: 'warning',
            })
            return
        }

        if (!this.newTokenName.trim()) {
            Swal.fire({
                title: 'Name Required',
                text: 'Please enter a name for the token',
                icon: 'warning',
            })
            return
        }

        this.isCreating = true
        this.createdPlainToken = null

        try {
            const result = await this.apiService.createToken({
                enterpriseKey: this.enterpriseKey,
                name: this.newTokenName.trim(),
                description: this.newTokenDescription.trim() || undefined,
            })

            if (result.success && result.plainToken) {
                this.createdPlainToken = result.plainToken
                this.newTokenName = ''
                this.newTokenDescription = ''
                await this.loadTokens()
                
                // Show success with warning about copying the token
                await Swal.fire({
                    title: 'Token Created!',
                    html: `
                        <p class="text-danger"><strong>Important:</strong> Copy this token now. It will not be shown again!</p>
                        <div class="alert alert-secondary">
                            <code style="word-break: break-all;">${result.plainToken}</code>
                        </div>
                    `,
                    icon: 'success',
                    confirmButtonText: 'I have copied the token',
                })
            } else {
                throw new Error(result.error || 'Failed to create token')
            }
        } catch (error: any) {
            console.error('Error creating token:', error)
            Swal.fire({
                title: 'Error',
                text: error.message || 'Failed to create API token',
                icon: 'error',
            })
        } finally {
            this.isCreating = false
        }
    }

    /**
     * Revoke a token
     */
    async revokeToken(token: ApiTokenListItem): Promise<void> {
        const confirmResult = await Swal.fire({
            title: 'Revoke Token?',
            html: `
                <p>Are you sure you want to revoke the token "<strong>${token.name}</strong>"?</p>
                <p class="text-muted">Token: ${token.tokenPrefix}...</p>
                <p class="text-danger">This action cannot be undone. Any applications using this token will stop working.</p>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, revoke it',
            confirmButtonColor: '#dc3545',
            cancelButtonText: 'Cancel',
        })

        if (!confirmResult.isConfirmed) return

        try {
            const result = await this.apiService.revokeToken(this.enterpriseKey, token.$key)
            
            if (result.success) {
                await Swal.fire({
                    title: 'Token Revoked',
                    text: 'The API token has been revoked',
                    icon: 'success',
                })
                await this.loadTokens()
            } else {
                throw new Error(result.error || 'Failed to revoke token')
            }
        } catch (error: any) {
            console.error('Error revoking token:', error)
            Swal.fire({
                title: 'Error',
                text: error.message || 'Failed to revoke API token',
                icon: 'error',
            })
        }
    }

    /**
     * Copy text to clipboard
     */
    copyToClipboard(text: string): void {
        navigator.clipboard.writeText(text).then(() => {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Copied to clipboard!',
                showConfirmButton: false,
                timer: 1500,
            })
        })
    }

    /**
     * Open API docs in new tab
     */
    openApiDocs(): void {
        window.open(this.apiDocsUrl, '_blank')
    }
}
